/**
 * Account Inspector — accounts, inside the hub.
 *
 * ⚠️ NOT ONLY RENEWALS (Josh, 2026-09-04): "It is also for accounts an agent is
 * trying to win. They can take the insured's policies from another agency and
 * run it through the inspector." Everything here said "renewal", which quietly
 * told half the users this screen was not for them.
 *
 * ⚠️ IT USED TO OPEN IN A NEW TAB, AND THAT IS WHAT THIS FIXES (Josh,
 * 2026-09-04: "the analyzer link takes you to a portal outside of the system and
 * I don't want that. I want it all inside the hub"). The module's `href` was an
 * absolute URL to a separate Railway deployment, so the rail entry left the hub
 * entirely — a different application, a different login, no way back.
 *
 * ⚠️ THE STANDALONE DEPLOYMENT IS NOT RETIRED. One agent uses it, and it reads
 * and writes the same database this page does. Nothing was copied, migrated or
 * synced — see apps/api/src/inspector.ts. An account created here appears there,
 * which is why the insert matches the standalone's row shape exactly.
 *
 * ⚠️ AND THE ROWS ARRIVE FROM THE HUB'S API, NOT FROM SUPABASE. The standalone's
 * pages query Supabase straight from the browser under RLS. Doing that here
 * would need an Inspector auth session in this browser — a second sign-in, and
 * new redirect URIs on the shared Entra registration, which is the one change
 * that can take the live apps down.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHero } from '../components/PageHero';
import { StatCard } from '../components/StatCard';
import { ApiError, api, type InspectorAccount } from '../lib/api';

/**
 * The Inspector's own lifecycle, with its own labels.
 *
 * ⚠️ FIVE STATUSES, NOT THE MOCK'S TWO. The mock shows "In Review" and
 * "Complete"; the table also holds intake, documents and analyzing, and an
 * account sitting in `documents` would have had to be drawn as one of the two
 * it is not. Borrowed from the Inspector's ACCOUNT_STATUSES rather than
 * reinvented, so both front doors describe a row the same way.
 */
const STATUS_LABEL: Record<string, string> = {
  intake: 'Intake',
  documents: 'Documents',
  analyzing: 'Analyzing',
  review: 'In Review',
  complete: 'Complete',
};

/** Tag modifier per status — named for the state, never for the colour. */
const STATUS_TAG: Record<string, string> = {
  intake: 'is-draft',
  documents: 'is-draft',
  analyzing: 'is-questionnaire',
  review: 'is-submitted',
  complete: 'is-online',
};

const shortDate = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? String(iso)
    : d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
};

/** `effective_date` is free text on the Inspector's form, so it is shown as given. */
const plainDate = (v: string | null): string => (v && v.trim() ? v.trim() : '—');

const docCount = (a: InspectorAccount): number =>
  Array.isArray(a.data?.documents) ? a.data!.documents!.length : 0;

const initialsOf = (name: string | null): string =>
  (name || '?')
    .replace(/\b(inc|llc|l\.l\.c|ltd|co|corp|company|the)\b\.?/gi, ' ')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('') || '?';

export function Inspector(): JSX.Element {
  const navigate = useNavigate();
  const [rows, setRows] = useState<InspectorAccount[] | null>(null);
  const [err, setErr] = useState('');
  const [configured, setConfigured] = useState<boolean | null>(null);

  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('all');

  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDate, setNewDate] = useState('');

  const load = () =>
    api
      .inspectorAccounts()
      .then((r) => {
        setRows(r.accounts);
        setErr('');
      })
      .catch((e: unknown) => {
        setRows([]);
        setErr(e instanceof ApiError ? e.message : 'Could not load your accounts.');
      });

  useEffect(() => {
    api
      .inspectorMe()
      .then((r) => setConfigured(r.configured))
      .catch(() => setConfigured(null));
    void load();
  }, []);

  /** Null until the fetch answers — the cards show a placeholder, not a zero. */
  const loading = rows === null;
  const all = useMemo(() => rows ?? [], [rows]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return all.filter(
      (a) =>
        (status === 'all' || a.status === status) &&
        (!q || `${a.named_insured ?? ''} ${a.id}`.toLowerCase().includes(q)),
    );
  }, [all, query, status]);

  const stats = useMemo(
    () => ({
      total: all.length,
      complete: all.filter((a) => a.status === 'complete').length,
      /* ⚠️ "IN PROGRESS", NOT THE MOCK'S "IN REVIEW". Counting only `review`
         would leave intake, documents and analyzing in no card at all — the
         mock's own numbers do not add up to its total for exactly that reason.
         Everything not finished is the number somebody acts on. */
      open: all.filter((a) => a.status !== 'complete').length,
      documents: all.reduce((n, a) => n + docCount(a), 0),
    }),
    [all],
  );

  const create = async () => {
    if (!newName.trim()) return;
    setBusy(true);
    try {
      await api.inspectorCreateAccount({
        namedInsured: newName.trim(),
        effectiveDate: newDate.trim(),
      });
      setCreating(false);
      setNewName('');
      setNewDate('');
      await load();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not create the account.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="wrap">
      <PageHero
        title="Account Inspector"
        sub="Upload an account's policies and loss runs — a renewal or one you are trying to win — and see what needs attention."
        action={
          <button className="btn btn-primary" type="button" onClick={() => setCreating((s) => !s)}>
            + New account
          </button>
        }
      />

      {err && <div className="empty">{err}</div>}
      <div className="stat-row">
        <StatCard
          tone="blue"
          icon="M6 3h9l4 4v14H6zM14 3v5h5M9 12h7M9 16h5"
          label="Total accounts"
          value={stats.total}
          note="Renewals and new business"
          loading={loading}
        />
        <StatCard
          tone="green"
          icon="M12 3a9 9 0 100 18 9 9 0 000-18zM8 12l3 3 5-5"
          label="Complete"
          value={stats.complete}
          note="Analysis finished"
          loading={loading}
        />
        <StatCard
          tone="amber"
          icon="M12 7v5l3 2M12 3a9 9 0 100 18 9 9 0 000-18z"
          label="In progress"
          value={stats.open}
          note="Not finished yet"
          loading={loading}
          slow={stats.open > 0}
        />
        <StatCard
          tone="violet"
          icon="M6 3h9l4 4v14H6zM14 3v5h5M9 13h6M9 17h4"
          label="Documents"
          value={stats.documents}
          note="Across all accounts"
          loading={loading}
        />
      </div>

      <section className="panel lr-table-panel">
        <div className="pu-panel" style={{ border: 0, boxShadow: 'none', margin: 0, paddingBottom: 0 }}>
          <div className="pu-head">
            <div>
              <h3>Accounts</h3>
              <p>Create one, upload the documents, and generate the analysis.</p>
            </div>
          </div>
        </div>

        {creating && (
          <div className="lr-drawer" style={{ alignItems: 'flex-end' }}>
            <div style={{ flex: '1 1 18rem' }}>
              <span>Named insured</span>
              <input
                className="lr-claiminput"
                style={{ width: '100%' }}
                autoFocus
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Legal name of the insured"
              />
            </div>
            <div style={{ flex: '0 1 12rem' }}>
              {/* Labelled the way the Inspector labels it. See InspectorAccount. */}
              <span>Effective date</span>
              <input
                className="lr-claiminput"
                style={{ width: '100%' }}
                value={newDate}
                onChange={(e) => setNewDate(e.target.value)}
                placeholder="mm/dd/yyyy"
              />
            </div>
            <div className="lr-drawer-actions">
              <button className="btn btn-sm btn-ghost" type="button" onClick={() => setCreating(false)}>
                Cancel
              </button>
              <button
                className="btn btn-sm btn-primary"
                type="button"
                disabled={!newName.trim() || busy}
                onClick={() => void create()}
              >
                {busy ? 'Creating…' : 'Create account'}
              </button>
            </div>
          </div>
        )}

        <div className="lr-toolbar">
          <div className="lr-search">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M11 4a7 7 0 100 14 7 7 0 000-14zM20 20l-4-4" />
            </svg>
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search accounts by name or account number…"
              aria-label="Search accounts"
            />
          </div>
          <div className="lr-filters">
            {/* ⚠️ NO "ALL MARKETS" FILTER. The mock has one; `accounts` carries no
                market, so it could only ever have filtered nothing. */}
            <select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
              <option value="all">All statuses</option>
              {Object.entries(STATUS_LABEL).map(([k, label]) => (
                <option key={k} value={k}>
                  {label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {rows === null ? (
          <p className="lr-empty">Loading…</p>
        ) : configured === false ? (
          <p className="lr-empty">
            The Inspector is not connected on this deployment yet.
            <span>
              Set INSPECTOR_SUPABASE_URL and INSPECTOR_SUPABASE_SERVICE_ROLE_KEY in Railway and this
              fills in. Until then the standalone Inspector is unaffected.
            </span>
          </p>
        ) : !all.length ? (
          <p className="lr-empty">
            No accounts yet.
            <span>
              Start one above. If you expected accounts here, your email may not be on an agency in
              the Inspector yet.
            </span>
          </p>
        ) : !visible.length ? (
          <p className="lr-empty">
            Nothing matches that.
            <span>Try another status, or clear the search.</span>
          </p>
        ) : (
          <>
            <table className="lr-table">
              <thead>
                <tr>
                  <th>Named insured</th>
                  <th>Status</th>
                  {/* Not "Expiry date" — see InspectorAccount in lib/api.ts. */}
                  <th>Effective</th>
                  <th>Documents</th>
                  <th>Created</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {visible.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <div className="lr-cell">
                        <span className="lr-avatar is-carrier" aria-hidden="true">
                          {initialsOf(a.named_insured)}
                        </span>
                        <div>
                          <b>{a.named_insured || 'Unnamed account'}</b>
                          <span>{a.id}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`tag ${STATUS_TAG[a.status] ?? 'is-draft'}`}>
                        {STATUS_LABEL[a.status] ?? a.status}
                      </span>
                    </td>
                    <td>
                      <b style={{ fontWeight: 600 }}>{plainDate(a.effective_date)}</b>
                    </td>
                    <td>
                      <b style={{ fontWeight: 600 }}>{docCount(a)}</b>
                    </td>
                    <td className="sp-when">
                      <b>{shortDate(a.created_at)}</b>
                      {a.created_by && <span>by {a.created_by.split('@')[0]}</span>}
                    </td>
                    <td className="sa-act">
                      <button
                        className="btn btn-ghost btn-sm"
                        type="button"
                        onClick={() => navigate(`/hub/inspector/${encodeURIComponent(a.id)}`)}
                      >
                        Open →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <p className="lr-count-line">
              Showing {visible.length} of {all.length} account{all.length === 1 ? '' : 's'}
            </p>
          </>
        )}
      </section>

      <section className="panel lr-about">
        <div>
          <h3>
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 3a9 9 0 100 18 9 9 0 000-18zM12 11v5M12 8h.01" />
            </svg>
            What the Inspector does
          </h3>
          <p>
            Give it a renewal — the policies and the loss runs you already have — and it returns an
            exposure workbook and an executive loss summary, built from the documents rather than
            re-keyed by hand.
          </p>
          <p className="lr-about-note">
            These accounts are read live from the Inspector. An account you start here is the same
            row the standalone Inspector shows.
          </p>
        </div>
        <div>
          <h3>Still on the standalone</h3>
          <ul>
            <li>Opening an account and uploading its documents</li>
            <li>Generating the exposure workbook and loss summary</li>
            <li>The client review screen</li>
            <li>Agency and team settings — the hub&rsquo;s Manage access covers those</li>
          </ul>
        </div>
      </section>
    </div>
  );
}
